import type { CareLogEntryType } from "@/api/careLog/types";
import { useTheme } from "@/theme/ThemeProvider";
import {
  ClipboardTextIcon,
  FlagIcon,
  HouseIcon,
  LinkSimpleIcon,
  ListChecksIcon,
  MapPinIcon,
  NotePencilIcon,
  PawPrintIcon,
  PillIcon,
  ScalesIcon,
  StethoscopeIcon,
  BuildingsIcon,
  UserIcon,
} from "phosphor-react-native";
import React from "react";
import { Text, View, ViewStyle } from "react-native";

/**
 * Einheitliche Bildsprache der App: jede Kern-Entität und jedes Protokoll-Konzept
 * hat genau EIN Icon und EINE Farbe. Einzige Quelle der Wahrheit – überall nutzen,
 * damit man auf einen Blick erkennt: Tier, Person oder Ort.
 */

export type IconComponent = React.ComponentType<{
  size?: number;
  color?: string;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
}>;

export type EntityKind = "animal" | "person" | "location" | "organization";

export const ENTITY_ICONS: Record<EntityKind, IconComponent> = {
  animal: PawPrintIcon,
  person: UserIcon,
  location: MapPinIcon,
  organization: BuildingsIcon,
};

export const ENTITY_LABELS: Record<EntityKind, string> = {
  animal: "Tier",
  person: "Person",
  location: "Ort",
  organization: "Verein",
};

/** Reserviert für Start/Navigation – bewusst getrennt von "location". */
export const HOME_ICON: IconComponent = HouseIcon;

type ColorPair = { fg: string; bg: string };

const ENTITY_COLORS_LIGHT: Record<EntityKind, ColorPair> = {
  animal: { fg: "#0F8C7E", bg: "rgba(20,148,136,0.12)" },
  person: { fg: "#6D4FC4", bg: "rgba(109,79,196,0.12)" },
  location: { fg: "#B86C12", bg: "rgba(184,108,18,0.12)" },
  organization: { fg: "#3F6F8F", bg: "rgba(63,111,143,0.12)" },
};

const ENTITY_COLORS_DARK: Record<EntityKind, ColorPair> = {
  animal: { fg: "#34C6B6", bg: "rgba(52,198,182,0.18)" },
  person: { fg: "#A78BFA", bg: "rgba(167,139,250,0.18)" },
  location: { fg: "#E0A33A", bg: "rgba(224,163,58,0.18)" },
  organization: { fg: "#84B0CC", bg: "rgba(132,176,204,0.18)" },
};

export function entityColors(kind: EntityKind, mode: "light" | "dark"): ColorPair {
  return (mode === "dark" ? ENTITY_COLORS_DARK : ENTITY_COLORS_LIGHT)[kind];
}

export function useEntityColors(kind: EntityKind): ColorPair {
  const { theme } = useTheme();
  return entityColors(kind, theme.mode === "dark" ? "dark" : "light");
}

/**
 * Icon einer Entität – optional als runder, eingefärbter Avatar.
 */
export function EntityIcon({
  kind,
  size = 24,
  avatar = false,
  avatarSize = 44,
  weight = "duotone",
  color,
}: {
  kind: EntityKind;
  size?: number;
  avatar?: boolean;
  avatarSize?: number;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
  color?: string;
}) {
  const colors = useEntityColors(kind);
  const Icon = ENTITY_ICONS[kind];
  const fg = color ?? colors.fg;

  if (!avatar) {
    return <Icon size={size} color={fg} weight={weight} />;
  }
  return (
    <View
      style={{
        width: avatarSize,
        height: avatarSize,
        borderRadius: avatarSize / 2,
        backgroundColor: colors.bg,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Icon size={size} color={fg} weight={weight} />
    </View>
  );
}

/**
 * Kompakter Chip "Icon + Label" zur Typ-Kennzeichnung in dichten Ansichten.
 */
export function EntityChip({ kind, label, style }: { kind: EntityKind; label?: string; style?: ViewStyle }) {
  const { theme } = useTheme();
  const colors = useEntityColors(kind);
  const Icon = ENTITY_ICONS[kind];
  return (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space.xs,
          paddingHorizontal: theme.space.sm,
          paddingVertical: 2,
          borderRadius: theme.radius.sm,
          backgroundColor: colors.bg,
          alignSelf: "flex-start",
        },
        style,
      ]}
    >
      <Icon size={13} color={colors.fg} weight="duotone" />
      <Text style={{ color: colors.fg, fontSize: theme.fontSize.tiny, fontWeight: "700" }}>
        {label ?? ENTITY_LABELS[kind]}
      </Text>
    </View>
  );
}

/* --------------------------------------------------------------------------
 * Sekundäre Konzepte: Protokoll-Eintragstypen
 * ------------------------------------------------------------------------ */

export const CARE_ENTRY_ICONS: Record<CareLogEntryType, IconComponent> = {
  weight: ScalesIcon,
  diagnosis: StethoscopeIcon,
  treatment: PillIcon,
  location_change: MapPinIcon,
  status_change: FlagIcon,
  activity: ListChecksIcon,
  text: NotePencilIcon,
};

/** Eigenständige Icons für Diagnose & Behandlung (auch außerhalb des Logs). */
export const DIAGNOSIS_ICON: IconComponent = StethoscopeIcon;
export const TREATMENT_ICON: IconComponent = PillIcon;
/** Verknüpfung Diagnose ↔ Behandlung (Policy). */
export const POLICY_ICON: IconComponent = LinkSimpleIcon;
export const WEIGHT_ICON: IconComponent = ScalesIcon;
export const STATUS_ICON: IconComponent = FlagIcon;
export const NOTE_ICON: IconComponent = NotePencilIcon;

// Fallback, falls ältere Stellen noch ClipboardText erwarten.
export const TREATMENT_PLAN_ICON: IconComponent = ClipboardTextIcon;
